// Лентата с търсачката на листа „Прехвърляне": голямата търсачка (същата като
// при доставка и изписване — пише се отвсякъде, ↑/↓ движат маркирания
// резултат, Enter добавя реда и фокусът отива в „Количество") + „Целият
// списък" (само продуктите с наличност в склада-източник). Колоните на
// резултатите: Продукт · Наличност в източника · Ед. цена.
import { useEffect } from "react";
import { Input } from "antd";
import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Product } from "../../../../api/inventory";
import { highlightWords } from "../../components/highlight";
import { formatQtyInput, formatUnitPrice } from "../../components/numbers";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";

// Един ред от резултатите — родителят добавя наличността и цената
export type TransferSearchRow = {
  product: Product;
  added: boolean;
  available: number;
  unitPrice: number | null;
  decimals: number;
};

// Продукт · Наличност · Ед. цена
const DROPDOWN_GRID_CLASS = "grid grid-cols-[1fr_150px_120px]";

type Props = {
  attachInputRef: (node: HTMLInputElement | null) => void;
  value: string;
  onValueChange: (value: string) => void;
  onFocusChange: (focused: boolean) => void;
  onInputKeyDown: (event: React.KeyboardEvent) => void;
  dropdownOpen: boolean;
  rows: TransferSearchRow[];
  searchWords: string[];
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onPick: (productId: number) => void;
  // „Целият списък" — без склад-източник няма какво да се покаже
  onOpenCatalog: () => void;
  catalogDisabled: boolean;
};

export const TransferWorksheetSearch = ({
  attachInputRef,
  value,
  onValueChange,
  onFocusChange,
  onInputKeyDown,
  dropdownOpen,
  rows,
  searchWords,
  highlightedIndex,
  onHighlightChange,
  onPick,
  onOpenCatalog,
  catalogDisabled,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();

  // Маркираният резултат винаги остава във видимата част на списъка
  useEffect(() => {
    if (!dropdownOpen) return;
    document
      .getElementById(`trx-opt-${highlightedIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, dropdownOpen]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <Input
          ref={(node) => attachInputRef(node?.input ?? null)}
          allowClear
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onFocus={() => onFocusChange(true)}
          onBlur={() => onFocusChange(false)}
          onKeyDown={onInputKeyDown}
          prefix={<SearchOutlined className="mr-1 text-[17px] text-muted" />}
          placeholder={t("inventoryPage.wsSearchPh")}
          // ! — antd cssinjs е извън Tailwind layer-ите и иначе бие класа;
          // 16px — под 16px iOS Safari зуумва страницата при фокус
          className={`w-full !rounded-[10px] ${isNarrow ? "!text-base" : "!text-[15px]"}`}
        />
        {dropdownOpen && (
          <div className="dk-elevated absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-shade-3 bg-surface shadow-[0_18px_44px_rgba(20,15,8,0.16),0_4px_12px_rgba(20,15,8,0.08)]">
            <div
              className={`${DROPDOWN_GRID_CLASS} items-center gap-[18px] border-b border-shade-2 bg-fill px-[18px] py-2 text-[10.5px] font-semibold uppercase leading-[normal] tracking-[0.07em] text-muted`}
            >
              <span className="truncate">
                {t("inventoryPage.wsResultsCount", { count: rows.length })}
              </span>
              <span className="whitespace-nowrap text-right">
                {t("inventoryPage.colAvailable")}
              </span>
              <span className="whitespace-nowrap text-right">
                {t("inventoryPage.colUnitPrice")}
              </span>
            </div>
            <div className="max-h-[min(46vh,430px)] overflow-y-auto">
              {rows.map((row, index) => (
                <div
                  key={row.product.id}
                  id={`trx-opt-${index}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onPick(row.product.id)}
                  onMouseMove={() => onHighlightChange(index)}
                  className={`${DROPDOWN_GRID_CLASS} cursor-pointer items-center gap-[18px] border-b border-shade-1 px-[18px] py-[11px] ${
                    index === highlightedIndex ? "bg-brand-tint" : "bg-surface"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="truncate text-[15px] font-semibold leading-[normal] text-ink">
                      {highlightWords(row.product.name, searchWords)}
                    </span>
                    {row.product.unit && (
                      <span className="shrink-0 text-xs leading-[normal] text-muted">
                        {row.product.unit}
                      </span>
                    )}
                    {row.added && (
                      <span className="shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] pb-[3px] pt-[2px] text-[11px] font-semibold leading-[normal] text-brand-deep-ink">
                        ✓ {t("inventoryPage.wsAddedTag")}
                      </span>
                    )}
                  </div>
                  <div className="whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-semibold leading-[normal] text-body">
                    {formatQtyInput(row.available, row.decimals)}
                    {row.product.unit ? (
                      <span className="font-sans text-xs font-normal text-muted">
                        {" "}
                        {row.product.unit}
                      </span>
                    ) : null}
                  </div>
                  <div
                    className={`whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[15px] font-bold leading-[normal] ${
                      row.unitPrice != null ? "text-brand-deep-ink" : "text-ghost"
                    }`}
                  >
                    {row.unitPrice != null ? formatUnitPrice(row.unitPrice) : "—"}
                  </div>
                </div>
              ))}
              {rows.length === 0 && (
                <div className="px-4 py-[18px] text-[12.5px] leading-[normal] text-muted">
                  {t("inventoryPage.wsNoMatch")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onOpenCatalog}
        disabled={catalogDisabled}
        className={`${SECONDARY_BUTTON_CLASS} !py-[9px]`}
      >
        <AppstoreOutlined />
        {t("inventoryPage.catalogOpen")}
      </button>
    </div>
  );
};

export default TransferWorksheetSearch;
